import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Facebook,
  Loader2,
  MessageSquare,
  RefreshCw,
  Settings2,
  TestTube2,
  Trash2,
  Users,
} from "lucide-react";
import { fetchClaw9Accounts } from "../api/claw9";
import { getOAuthUrl } from "../api/socialMedia";
import {
  deleteMessengerBotConfig,
  getMessengerBotConfigs,
  sendMessengerBotTest,
  updateMessengerBotConfig,
} from "../api/messengerBot";
import { CONNECTABLE_CHANNELS, getChannelLabel } from "../utils/messagingChannels";

const DEFAULT_TEST_MESSAGE = "What mortgage programs do you offer?";
const BRAND_STORAGE_KEY = "claw9_social_brand_id";

const normalizeAccounts = (result) => {
  const accounts = result?.accounts || result?.data?.accounts || result?.data || [];
  return Array.isArray(accounts) ? accounts : [];
};

const MessengerConfigCard = ({
  config,
  accountName,
  isExpanded,
  onToggle,
  onOpenInbox,
  onSave,
  onDelete,
  onTest,
}) => {
  const [form, setForm] = useState({
    bot_name: config.bot_name || "",
    welcome_message: config.welcome_message || "",
    language: config.language || "en",
    lead_capture_enabled: Boolean(config.lead_capture_enabled),
    active: Boolean(config.active),
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    setForm({
      bot_name: config.bot_name || "",
      welcome_message: config.welcome_message || "",
      language: config.language || "en",
      lead_capture_enabled: Boolean(config.lead_capture_enabled),
      active: Boolean(config.active),
    });
  }, [config]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(config.config_id, form);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTest = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await onTest(config.config_id, DEFAULT_TEST_MESSAGE);
      setTestResult({ type: "success", message: "Test message sent successfully." });
    } catch (error) {
      setTestResult({ type: "error", message: error.message || "Unable to send test message." });
    } finally {
      setIsTesting(false);
    }
  };

  return (
    <div className="bg-[#0d1424] border border-white/10 rounded-2xl overflow-hidden">
      <div className="p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#1877f2]/15 text-[#4d94ff] flex items-center justify-center flex-shrink-0">
            <Facebook className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-white">{config.page_name || "Connected source"}</h3>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                config.active ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/60"
              }`}>
                {config.active ? `${getChannelLabel(config.platform)} live` : "Disabled"}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-white/60">
              <span className="inline-flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                {getChannelLabel(config.platform)}
              </span>
              <span className="inline-flex items-center gap-2">
                <Users className="w-4 h-4" />
                {accountName || config.crm_account_id || "Auto-linked account"}
              </span>
              <span className="inline-flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Last sync: Active now
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={onOpenInbox}
            className="px-4 py-2 rounded-xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white text-sm font-medium transition-colors"
          >
            Open Inbox
          </button>
          <button
            onClick={onToggle}
            className="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
          >
            <Settings2 className="w-4 h-4" />
            {isExpanded ? "Hide Settings" : "Manage Settings"}
          </button>
          <button
            onClick={() => onDelete(config.config_id)}
            className="px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm font-medium transition-colors inline-flex items-center gap-2"
          >
            <Trash2 className="w-4 h-4" />
            Disconnect
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-white/10 p-5 grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Bot name</label>
              <input
                value={form.bot_name}
                onChange={(event) => setForm((prev) => ({ ...prev, bot_name: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-[#050810] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Welcome message</label>
              <textarea
                value={form.welcome_message}
                onChange={(event) => setForm((prev) => ({ ...prev, welcome_message: event.target.value }))}
                rows={4}
                className="w-full px-4 py-3 rounded-xl bg-[#050810] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-[#ff4d4d] resize-none"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-white/70 mb-2">Language</label>
              <select
                value={form.language}
                onChange={(event) => setForm((prev) => ({ ...prev, language: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl bg-[#050810] border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
              >
                <option value="en">English</option>
                <option value="vi">Vietnamese</option>
              </select>
            </div>

            <label className="flex items-center justify-between gap-3 p-4 rounded-xl bg-[#050810] border border-white/10">
              <div>
                <p className="text-sm font-medium text-white">Bot enabled</p>
                <p className="text-xs text-white/50">Allow AI replies in this messaging channel</p>
              </div>
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => setForm((prev) => ({ ...prev, active: event.target.checked }))}
                className="h-4 w-4"
              />
            </label>

            <label className="flex items-center justify-between gap-3 p-4 rounded-xl bg-[#050810] border border-white/10">
              <div>
                <p className="text-sm font-medium text-white">Lead capture enabled</p>
                <p className="text-xs text-white/50">Create or update CRM contacts from channel conversations</p>
              </div>
              <input
                type="checkbox"
                checked={form.lead_capture_enabled}
                onChange={(event) => setForm((prev) => ({ ...prev, lead_capture_enabled: event.target.checked }))}
                className="h-4 w-4"
              />
            </label>

            <div className="flex flex-col gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-4 py-3 rounded-xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white text-sm font-medium transition-colors disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save Settings"}
              </button>
              <button
                onClick={handleTest}
                disabled={isTesting}
                className="px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube2 className="w-4 h-4" />}
                Send Test Message
              </button>
            </div>

            {testResult && (
              <div className={`p-3 rounded-xl text-sm ${
                testResult.type === "success"
                  ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                  : "bg-red-500/10 text-red-300 border border-red-500/20"
              }`}>
                {testResult.message}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MessengerSetupPanel = ({ onOpenInbox }) => {
  const [configs, setConfigs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectingChannel, setConnectingChannel] = useState("");
  const [error, setError] = useState(null);
  const [expandedConfigId, setExpandedConfigId] = useState(null);
  const [brandId, setBrandId] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(BRAND_STORAGE_KEY) || "";
  });

  // Auto-populate brandId from first account if not set
  useEffect(() => {
    if (!brandId && accounts.length > 0) {
      const defaultAccountId = accounts[0].account_id;
      if (defaultAccountId) {
        setBrandId(defaultAccountId);
      }
    }
  }, [accounts, brandId]);

  const accountMap = useMemo(
    () => new Map(accounts.map((account) => [account.account_id, account.display_name || account.name || account.account_id])),
    [accounts]
  );

  const getActionableError = useCallback((message) => {
    const text = String(message || "");
    const normalized = text.toLowerCase();

    if (normalized.includes("subscription") || normalized.includes("ultra") || normalized.includes("enterprise")) {
      return "Messaging channels require an Ultra or Enterprise subscription.";
    }
    if (normalized.includes("brand")) {
      return "Select or enter a brand ID before connecting Facebook.";
    }
    if (normalized.includes("token") || normalized.includes("auth") || normalized.includes("sign in")) {
      return "Your session is not authorized for messaging setup. Sign in again and retry.";
    }
    if (normalized.includes("404") || normalized.includes("not found")) {
      return "Messaging integration is not available yet. Please contact support for setup.";
    }
    return text || "Unable to complete messaging setup.";
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [configResult, accountResult] = await Promise.all([
        getMessengerBotConfigs(),
        fetchClaw9Accounts().catch(() => ({ accounts: [] })),
      ]);

      setConfigs(configResult.configs || []);
      setAccounts(normalizeAccounts(accountResult));
    } catch (loadError) {
      setError(getActionableError(loadError.message || "Failed to load messaging setup."));
    } finally {
      setLoading(false);
    }
  }, [getActionableError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refreshFlag = sessionStorage.getItem("claw9_messaging_setup_success");
    if (refreshFlag) {
      sessionStorage.removeItem("claw9_messaging_setup_success");
      loadData();
    }
  }, [loadData]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (brandId.trim()) {
      window.localStorage.setItem(BRAND_STORAGE_KEY, brandId.trim());
    } else {
      window.localStorage.removeItem(BRAND_STORAGE_KEY);
    }
  }, [brandId]);

  const handleConnect = async (channel) => {
    if (!brandId.trim()) {
      setError("Select or enter a brand ID before connecting Facebook.");
      return;
    }

    setConnectingChannel(channel.id);
    setError(null);
    try {
      const response = await getOAuthUrl(channel.oauthPlatform, brandId.trim());
      const authUrl = response.authUrl || response.url;
      const state = response.state || "";

      if (!authUrl) {
        throw new Error("OAuth URL was not returned by the server.");
      }

      sessionStorage.setItem("claw9_messaging_flow", "connected-messaging");
      sessionStorage.setItem("claw9_messaging_channel", channel.id);
      sessionStorage.setItem("claw9_oauth_platform", channel.oauthPlatform);
      if (state) {
        sessionStorage.setItem("claw9_messaging_oauth_state", state);
      }

      window.location.href = authUrl;
    } catch (connectError) {
      setError(getActionableError(connectError.message || "Unable to start channel connection."));
      setConnectingChannel("");
    }
  };

  const handleSave = async (configId, updates) => {
    const updated = await updateMessengerBotConfig(configId, updates);
    setConfigs((prev) => prev.map((config) => (config.config_id === configId ? updated : config)));
  };

  const handleDelete = async (configId) => {
    const confirmed = window.confirm("Disconnect this Messenger page from Claw9?");
    if (!confirmed) return;

    await deleteMessengerBotConfig(configId);
    setConfigs((prev) => prev.filter((config) => config.config_id !== configId));
    if (expandedConfigId === configId) {
      setExpandedConfigId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[420px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#ff4d4d]" />
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto rounded-3xl bg-[#0a0f1c] border border-white/10">
      <div className="p-6 lg:p-8 border-b border-white/10 bg-gradient-to-br from-[#111827] via-[#101828] to-[#1e1b4b]">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/70 text-xs font-medium mb-4">
            <Facebook className="w-3.5 h-3.5" />
            Channel-aware CRM inbox setup
          </div>
          <h1 className="text-3xl font-semibold text-white">Connect messaging channels once</h1>
          <p className="mt-3 text-white/70 max-w-2xl">
            Connect a supported messaging channel, let Claw9 provision the CRM sync automatically, then route new conversations directly into the inbox.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={loadData}
              className="px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-medium transition-colors inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-6">
        {error && (
          <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-300 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <p>{error}</p>
          </div>
        )}

        {/* Account Selection - only show if multiple accounts or no account selected */}
        {(accounts.length > 1 || !brandId) && (
          <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/10">
            <h2 className="text-lg font-medium text-white">Select Account</h2>
            <p className="mt-2 text-sm text-white/60">
              Choose which account to connect your messaging channels to.
            </p>
            <div className="mt-4">
              {accounts.length > 0 ? (
                <select
                  value={brandId}
                  onChange={(event) => setBrandId(event.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-[#050810] border border-white/10 text-white focus:outline-none focus:ring-2 focus:ring-[#ff4d4d]"
                >
                  <option value="">Select an account...</option>
                  {accounts.map((account) => (
                    <option key={account.account_id} value={account.account_id}>
                      {account.display_name || account.name || account.account_id}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-amber-400 text-sm">No accounts found. Please set up an account first.</p>
              )}
            </div>
          </div>
        )}

        {configs.length === 0 ? (
          <div className="p-8 rounded-3xl border border-dashed border-white/10 bg-[#050810] text-center">
            <MessageSquare className="w-12 h-12 mx-auto text-white/30 mb-4" />
            <h2 className="text-xl font-semibold text-white">No messaging channels connected yet</h2>
            <p className="mt-2 text-white/60 max-w-xl mx-auto">
              Connect a supported channel to start syncing conversations into Claw9 chat and CRM. No token or webhook fields are required.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {configs.map((config) => (
              <MessengerConfigCard
                key={config.config_id}
                config={config}
                accountName={accountMap.get(config.crm_account_id)}
                isExpanded={expandedConfigId === config.config_id}
                onToggle={() => setExpandedConfigId((current) => (current === config.config_id ? null : config.config_id))}
                onOpenInbox={() => onOpenInbox?.(config.platform)}
                onSave={handleSave}
                onDelete={handleDelete}
                onTest={sendMessengerBotTest}
              />
            ))}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          {CONNECTABLE_CHANNELS.map((channel) => (
            <div key={channel.id} className="p-5 rounded-2xl bg-[#0d1424] border border-white/10">
              <div className="w-10 h-10 rounded-xl bg-[#1877f2]/15 text-[#4d94ff] flex items-center justify-center mb-3">
                <Facebook className="w-5 h-5" />
              </div>
              <h3 className="text-white font-medium">{channel.label}</h3>
              <p className="mt-2 text-sm text-white/60">{channel.description}</p>
              <button
                onClick={() => handleConnect(channel)}
                disabled={connectingChannel !== "" || !brandId.trim()}
                className="mt-4 px-4 py-2.5 rounded-xl bg-[#ff4d4d] hover:bg-[#ff6666] text-white text-sm font-medium transition-colors inline-flex items-center gap-2 disabled:opacity-60"
              >
                {connectingChannel === channel.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Facebook className="w-4 h-4" />}
                Connect {channel.label}
              </button>
            </div>
          ))}
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/10">
            <div className="w-10 h-10 rounded-xl bg-[#1877f2]/15 text-[#4d94ff] flex items-center justify-center mb-3">
              <Facebook className="w-5 h-5" />
            </div>
            <h3 className="text-white font-medium">Connected source</h3>
            <p className="mt-2 text-sm text-white/60">Users authenticate with the source platform and Claw9 handles the inbox mapping path automatically.</p>
          </div>
          <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/10">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h3 className="text-white font-medium">CRM inbox sync</h3>
            <p className="mt-2 text-sm text-white/60">Claw9 provisions the messaging config and enables CRM sync without exposing channel tokens or webhook internals.</p>
          </div>
          <div className="p-5 rounded-2xl bg-[#0d1424] border border-white/10">
            <div className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center mb-3">
              <ExternalLink className="w-5 h-5" />
            </div>
            <h3 className="text-white font-medium">Shared inbox</h3>
            <p className="mt-2 text-sm text-white/60">Channel conversations appear in the same Claw9 chat and CRM surfaces used for other inbox sources.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessengerSetupPanel;
